import { Routes } from '@angular/router';
import { customerAuthGuard } from './data/auth.guard';
import { OrderingLayoutComponent } from './layout/ordering-layout';

/**
 * Public online-ordering portal (orderyaa.com/ordering → future customer.orderyaa.com).
 * Self-contained: every page renders inside OrderingLayoutComponent (its own red/black header +
 * footer), NOT the marketing site's master layout. Lazy-loaded subtree.
 */
export const ORDERING_ROUTES: Routes = [
  {
    path: '',
    component: OrderingLayoutComponent,
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'discover' },
      {
        // The storefront's landing page: nearby shops grouped by store type, with the item-category
        // tree. Both designs stay mounted at their own URLs and the header carries a toggle between
        // them, so /stores is one click away and every existing /stores link still resolves.
        path: 'discover',
        loadComponent: () => import('./discover-page/discover-page').then((m) => m.DiscoverPageComponent),
      },
      {
        // The classic list design: one flat list with a filter rail. Kept as a full peer of
        // /discover, not a legacy alias — they share data services only, never markup.
        path: 'stores',
        loadComponent: () => import('./stores-page/stores-page').then((m) => m.StoresPageComponent),
      },
      {
        path: 'store/:id',
        loadComponent: () => import('./store-page/store-page').then((m) => m.StorePageComponent),
      },
      {
        path: 'services',
        loadComponent: () => import('./coming-soon/coming-soon').then((m) => m.ComingSoonComponent),
        data: { title: 'Services', subtitle: 'Book home and professional services near you.', icon: '🧰' },
      },
      {
        path: 'bookings',
        loadComponent: () => import('./coming-soon/coming-soon').then((m) => m.ComingSoonComponent),
        data: { title: 'Bookings', subtitle: 'Reserve appointments and slots from nearby stores.', icon: '📅' },
      },
      {
        path: 'cart',
        loadComponent: () => import('./cart-page/cart-page').then((m) => m.CartPageComponent),
      },
      {
        path: 'checkout',
        loadComponent: () => import('./checkout-page/checkout-page').then((m) => m.CheckoutPageComponent),
      },
      {
        path: 'login',
        loadComponent: () => import('./login-page/login-page').then((m) => m.LoginPageComponent),
      },
      {
        path: 'account',
        canActivate: [customerAuthGuard],
        loadComponent: () => import('./account-page/account-page').then((m) => m.AccountPageComponent),
      },
      {
        // British spelling in the URL to match the copy the storefront uses everywhere else.
        path: 'favourites',
        canActivate: [customerAuthGuard],
        loadComponent: () =>
          import('./favourites-page/favourites-page').then((m) => m.FavouritesPageComponent),
      },
      {
        path: 'orders',
        canActivate: [customerAuthGuard],
        loadComponent: () =>
          import('./order-history-page/order-history-page').then((m) => m.OrderHistoryPageComponent),
      },
      {
        path: 'transactions',
        canActivate: [customerAuthGuard],
        loadComponent: () =>
          import('./transactions-page/transactions-page').then((m) => m.TransactionsPageComponent),
      },
      {
        path: 'rewards',
        canActivate: [customerAuthGuard],
        loadComponent: () => import('./rewards-page/rewards-page').then((m) => m.RewardsPageComponent),
      },
      {
        // "Send feedback" — a problem report or suggestion about the app, for the platform team.
        path: 'feedback',
        canActivate: [customerAuthGuard],
        loadComponent: () => import('./feedback-page/feedback-page').then((m) => m.FeedbackPageComponent),
      },
      {
        path: 'order/:id',
        canActivate: [customerAuthGuard],
        loadComponent: () => import('./order-track-page/order-track-page').then((m) => m.OrderTrackPageComponent),
      },
    ],
  },
];
